import React from 'react';

import { sanitizeDataForSSR } from '@confluence/ssr-utilities';

type RenderCallback = () => React.ReactNode;

const pending: RenderCallback[] = [];
function queueRenderCallback(callback: RenderCallback) {
	pending.push(callback);
}

let inject = queueRenderCallback;

/**
 * Bootstrap script that is rendered inline before any data streams are emitted
 *
 * This initializes the following:
 * * window.__s - A map of stream names to a list of emitted values
 * * window.__sL - A map of stream names to a set of listeners
 * * window.__s_r - A function that initializes a stream if it doesn't exist
 * * window.__s__e - A function that emits a value to a stream
 */
const streamBootstrap = `
    if (!window.__s) {
        window.__s = {}; 
    }
    if (!window.__sL) {
        window.__sL = {}; 
    }
    window.__s_r = (s) => { 
        if (!window.__s[s]) { 
            window.__s[s] = []; 
        } 
        if (!window.__sL[s]) { 
            window.__sL[s] = new Set(); 
        } 
    }
    window.__s__e = (s, v) => { 
        try {
            var value = JSON.parse(v);
            if (!window.__s[s]) { 
                window.__s[s] = []; 
            } 
            window.__s[s].push(value); 
			if (window.__sL[s]) {
				window.__sL[s].forEach(fn => fn(value));
			}
        } catch (e) {
            console.error('[__s__e] error', e);
        }
    }`;

/**
 * Fix escape codes in a string to be encoded in JSON
 *
 * TODO: Backport this to sanitizeDataForSSR
 * @param value - The string to fix
 * @returns The string with escape codes fixed
 */
function sanitizeEscapeCodes(value: string) {
	return value.replaceAll('\\', '\\\\');
}

/**
 * Set the function to use for injecting content into the SSR stream
 * @param callback - The callback to set the inject function to
 *
 * This will immediately emit any pending callbacks
 */
export function setStreamInject(callback: (cb: RenderCallback) => void) {
	inject = callback;

	if (pending.length > 0) {
		pending.forEach(inject);
		pending.length = 0;
	}
}

export function clearStreamInject() {
	inject = queueRenderCallback;
}

const streams = new Map<string, EventStream<any>>();
/**
 * Clear all streams
 */
export function clearServerStreams() {
	streams.clear();
}

class EventStream<T extends object> {
	private initialized = false;
	private stream: string;

	constructor(stream: string) {
		this.stream = stream;
	}

	emit(value: T) {
		inject(() => {
			let script = '';
			if (!this.initialized) {
				script = `window.__s_r("${this.stream}");`;
				this.initialized = true;
			}

			const data = JSON.stringify(value);
			const sanitizedData = sanitizeEscapeCodes(data);

			script += `window.__s__e("${this.stream}", \"${sanitizeDataForSSR(sanitizedData)}\");`;
			return React.createElement('script', {
				dangerouslySetInnerHTML: {
					__html: script,
				},
			});
		});
	}
}

export function getServerStream<T extends object>(key: string): EventStream<T> {
	if (streams.size === 0) {
		inject(() => {
			return React.createElement('script', {
				dangerouslySetInnerHTML: {
					__html: streamBootstrap,
				},
			});
		});
	}

	let stream = streams.get(key);
	if (!stream) {
		stream = new EventStream(key);
		streams.set(key, stream);
	}
	return stream;
}
